import { t } from "@lingui/macro";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
} from "@reactive-resume/ui";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";
import { usePrintResume } from "@/client/services/resume";
import { useCrBalance } from "@/client/services/credit/balance";
import {
  isPromoCreditExpired,
  PROMO_CREDIT_ADVICE,
  PROMO_CREDIT_EXPIRED,
} from "@/client/services/credit/expiry";
import { useUser } from "@/client/services/user";
import { toast } from "@/client/hooks/use-toast";
import { notifyRelationshipChange } from "@/client/services/notification";
import { useEffect, useState } from "react";
import { triggerPdfDownload } from "../../../../services/resume/download-cta";

export const ConfirmDownloadDialog = () => {
  const { isOpen, close } = useDialog("confirm-download");
  const resume = useResumeStore((s) => s.resume);
  const { user } = useUser();
  const { credit: crBalance, error: crError } = useCrBalance(user?.id);
  const { printResume, loading: printLoading } = usePrintResume();
  const [localLoading, setLocalLoading] = useState(false);

  const openForfait = useDialog("forfait").open;

  const creditLoading = crBalance === undefined && !crError;
  const creditEmpty = crBalance?.credit === 0;
  const promoExpired = isPromoCreditExpired(crBalance);

  // Si l'utilisateur n'a plus de crédit, inutile de montrer la confirmation :
  // on le dirige directement vers le choix de formule (forfait), puis paiement.
  useEffect(() => {
    if (isOpen && creditEmpty && !crError) {
      if (promoExpired) {
        toast({ variant: "error", title: PROMO_CREDIT_EXPIRED, description: PROMO_CREDIT_ADVICE });
      }
      close();
      openForfait("create", { id: "forfait", item: { type: "download" } });
    }
  }, [isOpen, creditEmpty, crError, promoExpired, close, openForfait]);

  const onConfirm = async () => {
    setLocalLoading(true);

    try {
      const balance = crBalance?.credit ?? 0;
      if (balance <= 0) {
        localStorage.setItem("to_print", resume.id);
        openForfait("create", { id: "forfait", item: { type: "download" } });
        close();
        return;
      }

      const { url } = await printResume({ id: resume.id });

      // Téléchargement silencieux dans l'onglet principal + confirmation.
      // Le serveur sert le PDF en pièce jointe : aucune popup nécessaire.
      triggerPdfDownload(url);

      // Changement de relation (CV téléchargé) : réactive les invitations
      // SoftPrompt refusées (re-proposition web.dev).
      notifyRelationshipChange("cv_completed");

      toast.success("Téléchargement réussi 🎉", {
        description: "Votre CV est disponible dans vos téléchargements.",
        duration: 5_000,
      });

      close();
    } catch (error: any) {
      const message =
        error?.message ?? t`Une erreur est survenue lors de la génération du PDF (.pdf).`;
      if (/crédit|credit|balance/i.test(message)) {
        localStorage.setItem("to_print", resume.id);
        openForfait("create", { id: "forfait", item: { type: "download" } });
      } else {
        toast({ variant: "error", title: "Téléchargement échoué", description: message });
      }
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Télécharger le CV</DialogTitle>
        </DialogHeader>

        <div className="px-2 py-4">
          <p className="text-sm text-text-secondary">{t`Le CV sera généré en PDF (.pdf) puis téléchargé automatiquement. Vous resterez dans votre espace.`}</p>
          {creditEmpty && (
            <p className="text-danger mt-2 text-sm">
              {promoExpired
                ? `${PROMO_CREDIT_EXPIRED} ${PROMO_CREDIT_ADVICE}`
                : "Vous avez utilisé tous vos téléchargements. Obtenez-en plus pour continuer."}
            </p>
          )}
        </div>

        <DialogFooter>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={close} disabled={printLoading || localLoading}>
              Annuler
            </Button>
            <Button
              variant="primary"
              className="rounded-lg"
              onClick={onConfirm}
              disabled={creditLoading || printLoading || localLoading || creditEmpty}
            >
              {printLoading || localLoading ? (
                <span className="flex items-center gap-2">
                  <span className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Génération…
                </span>
              ) : (
                "Confirmer"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDownloadDialog;
